import { supabase } from "@/integrations/supabase/client";
import { invokeFunction } from "@/lib/utils/invokeFunction";
import { OrderConfirmationEmail } from "@/components/email-templates/OrderConfirmationEmail";
import { SellerNotificationEmail } from "@/components/email-templates/SellerNotificationEmail";
import { PremiumPurchaseEmail } from "@/components/email-templates/PremiumPurchaseEmail";
import { CreditsConfirmationEmail } from "@/components/email-templates/CreditsConfirmationEmail";
import { ListingCreationEmail } from "@/components/email-templates/ListingCreationEmail";
import * as logger from '@/lib/utils/logger'; // Import the logger

// Function to send confirmation email after listing creation
export const sendListingCreationEmail = async (
  email: string,
  username: string,
  listingTitle: string
): Promise<boolean> => {
  try {
    // Generate HTML email content using the ListingCreationEmail component
    const emailContent = ListingCreationEmail({
      userName: username,
      listingTitle: listingTitle
    });

    // Use the edge function to send email with proper type and HTML template
    const { data, error } = await supabase.functions.invoke(
      "send-email",
      {
        body: {
          to: email,
          type: "listing_creation",
          data: {
            emailTemplate: emailContent
          }
        }
      }
    );

    if (error) {
      console.error("Error sending listing confirmation email:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error sending email:", error);
    return false;
  }
};

// Store contact request from unauthenticated users
export const storeListingContactRequest = async (
  data: {
    listing_id: number;
    sender_name: string;
    sender_email: string;
    recipient_email: string;
    message: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from("listing_contact_requests")
      .insert(data);

    if (error) {
      console.error("Error storing contact request:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Add the missing email functions
export const sendOrderConfirmationEmail = async (orderDetails: any): Promise<boolean> => {
  logger.info("Attempting to send order confirmation email", { orderId: orderDetails.id });
  try {
    if (!orderDetails.buyer_email) {
      logger.error("Buyer email is missing from order details for order confirmation", { orderId: orderDetails.id });
      return false;
    }

    const emailContent = OrderConfirmationEmail({
      orderDetails: {
        id: orderDetails.id,
        created_at: orderDetails.created_at,
      },
      buyerName: orderDetails.buyer_name || "Customer",
      productTitle: orderDetails.listing_title || "Product",
      amount: orderDetails.amount || 0,
      currency: orderDetails.currency || "EUR",
      shippingMethod: orderDetails.shipping_method || "Standard"
    });

    logger.debug("Order confirmation email content generated", { orderId: orderDetails.id, buyerEmail: orderDetails.buyer_email });

    const { data, error } = await supabase.functions.invoke(
      "send-email",
      {
        body: {
          to: orderDetails.buyer_email,
          type: "order_confirmation",
          data: { // Ensure emailTemplate is within a 'data' object
            emailTemplate: emailContent
          }
        }
      }
    );

    if (error) {
      logger.error("Error invoking send-email function for order confirmation", { orderId: orderDetails.id, errorDetails: error });
      return false;
    }
    logger.info("Order confirmation email sent successfully via Edge Function", { orderId: orderDetails.id, responseData: data });
    return true;
  } catch (error) {
    logger.error("Unexpected error in sendOrderConfirmationEmail", { orderId: orderDetails.id, errorDetails: error });
    return false;
  }
};

export const sendSellerNotificationEmail = async (orderDetails: any): Promise<boolean> => {
  logger.info("Attempting to send seller notification email", { orderId: orderDetails.id });
  try {
    if (!orderDetails.seller_email) {
      logger.error("Seller email is missing from order details for seller notification", { orderId: orderDetails.id });
      return false;
    }

    // Parse delivery address from the order details
    let deliveryAddress = "Not provided";
    let buyerPhone = "";
    let buyerName = orderDetails.buyer_name || "Customer";
    
    try {
      if (orderDetails.delivery_address && typeof orderDetails.delivery_address === 'string') {
        const addressObj = JSON.parse(orderDetails.delivery_address);
        deliveryAddress = `${addressObj.address || ''}, ${addressObj.city || ''} ${addressObj.postalCode || ''}`.trim();
        buyerPhone = addressObj.phoneNumber || '';
        if (addressObj.fullName) {
          buyerName = addressObj.fullName;
        }
      }
    } catch (e) {
      logger.warn("Error parsing delivery address for seller notification", { orderId: orderDetails.id, errorDetails: e });
    }

    const emailContent = SellerNotificationEmail({
      orderDetails: {
        id: orderDetails.id,
        created_at: orderDetails.created_at,
      },
      sellerName: orderDetails.seller_name || "Seller",
      productTitle: orderDetails.listing_title || "Product",
      amount: orderDetails.amount || 0,
      currency: orderDetails.currency || "EUR",
      shippingMethod: orderDetails.shipping_method || "Standard",
      buyerInfo: {
        name: buyerName,
        address: deliveryAddress,
        phoneNumber: buyerPhone
      }
    });
    logger.debug("Seller notification email content generated", { orderId: orderDetails.id, sellerEmail: orderDetails.seller_email });

    const { data, error } = await supabase.functions.invoke(
      "send-email",
      {
        body: {
          to: orderDetails.seller_email,
          type: "seller_notification",
          data: { // Ensure emailTemplate is within a 'data' object
            emailTemplate: emailContent
          }
        }
      }
    );

    if (error) {
      logger.error("Error invoking send-email function for seller notification", { orderId: orderDetails.id, errorDetails: error });
      return false;
    }
    logger.info("Seller notification email sent successfully via Edge Function", { orderId: orderDetails.id, responseData: data });
    return true;
  } catch (error) {
    logger.error("Unexpected error in sendSellerNotificationEmail", { orderId: orderDetails.id, errorDetails: error });
    return false;
  }
};

export const sendPremiumPurchaseEmail = async (
  email: string,
  username: string,
  listingTitle: string
): Promise<boolean> => {
  try {
    const emailContent = PremiumPurchaseEmail({
      userName: username,
      listingTitle: listingTitle
    });

    const { data, error } = await supabase.functions.invoke(
      "send-email",
      {
        body: {
          to: email,
          subject: "Premium Purchase Confirmation - Treguim.org",
          type: "premium_purchase",
          emailTemplate: emailContent
        }
      }
    );

    if (error) {
      console.error("Error sending premium purchase email:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error sending premium purchase email:", error);
    return false;
  }
};

export const sendCreditsConfirmationEmail = async (
  email: string,
  username: string,
  credits: number,
  amount: number,
  currency: string
): Promise<boolean> => {
  try {
    const emailContent = CreditsConfirmationEmail({
      userName: username,
      credits: credits,
      amount: amount,
      currency: currency
    });

    const { data, error } = await supabase.functions.invoke(
      "send-email",
      {
        body: {
          to: email,
          subject: "Credits Purchase Confirmation - Treguim.org",
          type: "credits_purchase",
          emailTemplate: emailContent
        }
      }
    );

    if (error) {
      console.error("Error sending credits confirmation email:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error sending credits confirmation email:", error);
    return false;
  }
};
